import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Sparkles, X } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { notify } from '@/components/ui/notify';
import { formatTime } from '@/lib/format';
import { readStored, storageKey, writeStored } from '@/lib/storage';
import { cn } from '@/lib/utils';

/*
  Morning brief (02 #10) — the top of the Dashboard: the 3–4 things that need a decision today, in a sentence or
  two, from live data (GET /api/dashboard/morning-brief → backend/morning_brief.py, the same brief as the AI page).
  Built once per day (the 07:00 job, or the first visit) and only from the modules this person's role may open.
  When AI extras is on (`ai_extras`, Admin › Integrations, default off) the job also words it in two sentences
  (`ai_summary`); otherwise the facts are listed as they are. "Ask AI" opens the AI page (which shows the brief with a follow-up question per line);
  × hides it until tomorrow on this device — for this person only (a shared site tablet shows the next person theirs).
  Under "Mine" (02 #5) the brief is the one of the person's own projects (`?scope=mine`), so it never contradicts the
  Mine numbers below it. On a wide screen the plain wording keeps to two lines: the detail of the first two facts
  (the rest are in "Needs you today"), and "Read all" for everything.
*/

const DETAILED_FACTS = 2;

/** Per person: one person's × must not hide the brief for whoever signs in next on the same device. */
export const briefHiddenKey = (userId) => storageKey('dashboard', 'brief-hidden', userId || 'anonymous');

/**
 * Facts → one paragraph: "**3 LPOs 194+ days late** — AED 475,000 · Carrier Gulf …; **16 stages …** — …."
 * `detailed` = how many facts keep their detail (the rest show their title only); all by default.
 */
export function BriefSentence({ items, detailed = Infinity }) {
  return items.map((item, index) => (
    <Fragment key={item.key || index}>
      {index > 0 ? '; ' : ''}
      {item.url ? (
        <Link
          to={item.url}
          className="rounded font-bold text-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`morning-brief-item-${item.key}`}
        >
          {item.title}
        </Link>
      ) : <b className="font-bold">{item.title}</b>}
      {item.sub && index < detailed ? <span className="text-ink-2"> — {item.sub}</span> : null}
      {index === items.length - 1 ? '.' : ''}
    </Fragment>
  ));
}

export function MorningBriefCard({ isPhone = false, scope = 'everyone' }) {
  const { user } = useAuth() || {};
  const hiddenKey = briefHiddenKey(user?.id);
  const [brief, setBrief] = useState(null);
  const [state, setState] = useState('loading');
  const [hidden, setHidden] = useState(() => ({ key: hiddenKey, day: readStored(hiddenKey, '') }));
  // Someone else signed in on this device (the shell stays mounted): read their choice, not the last person's.
  const hiddenDay = hidden.key === hiddenKey ? hidden.day : readStored(hiddenKey, '');
  const setHiddenDay = (day) => setHidden({ key: hiddenKey, day });
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef(null);
  const requestRef = useRef(0);
  const mineScope = scope === 'mine';

  const load = useCallback(async (mine) => {
    const request = ++requestRef.current;
    try {
      const res = await api.get('/dashboard/morning-brief', mine ? { params: { scope: 'mine' } } : undefined);
      if (request !== requestRef.current) return;
      setBrief({ ...res.data, scope: res.data?.scope || (mine ? 'mine' : 'everyone') });
      setState('ready');
    } catch {
      // The brief is a convenience on top of the numbers below: if it cannot load, the page does without it.
      if (request === requestRef.current) setState('failed');
    }
  }, []);

  useEffect(() => { load(mineScope); }, [load, mineScope]);

  // Wide screens: is the (clamped) wording longer than its two lines? Then "Read all" shows it whole.
  const clampDesktop = !isPhone && !expanded;
  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node || !clampDesktop) return undefined;
    const measure = () => {
      const next = node.scrollHeight > node.clientHeight + 1;
      setOverflowing((previous) => (previous === next ? previous : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [clampDesktop, brief, state]);

  if (state === 'loading') {
    return <Skeleton className="h-[88px] w-full rounded-card" data-testid="morning-brief-loading" />;
  }
  if (state !== 'ready' || !brief || (brief.day && hiddenDay === brief.day)) return null;

  const items = Array.isArray(brief.items) ? brief.items : [];
  // Still showing the other scope's brief while this one loads: dimmed, never presented as the current one.
  const stale = brief.scope !== (mineScope ? 'mine' : 'everyone');
  const briefMine = brief.scope === 'mine';
  // "Ask AI" opens the AI page with the brief's first follow-up question in the box (not sent until asked).
  const firstQuestion = items.find((item) => item?.question)?.question || '';
  const askLink = firstQuestion ? `/ai?ask=${encodeURIComponent(firstQuestion)}` : '/ai';
  const aiText = String(brief.ai_summary || '').trim();
  const time = brief.generated_at ? formatTime(brief.generated_at) : '';
  const hide = () => {
    writeStored(hiddenKey, brief.day);
    setHiddenDay(brief.day);
    notify.success('Morning brief hidden until tomorrow', {
      undo: () => {
        writeStored(hiddenKey, '');
        setHiddenDay('');
      },
      undoneMessage: 'Morning brief is back',
    });
  };

  const label = [
    'Morning brief',
    time ? `generated ${time} from live data` : 'from live data',
    aiText ? 'worded by AI' : null,
    briefMine ? 'your projects' : null,
  ].filter(Boolean).join(' · ');
  // Wide screens, plain wording: the first facts keep their detail, the rest only their title (until "Read all").
  const hiddenDetail = !aiText && items.slice(DETAILED_FACTS).some((item) => item?.sub);
  const canExpandDesktop = !isPhone && (expanded || hiddenDetail || overflowing);
  const toggle = (
    <button
      type="button"
      onClick={() => setExpanded((value) => !value)}
      aria-expanded={expanded}
      aria-controls="morning-brief-text"
      className={cn(
        'inline-flex items-center gap-1 rounded text-[13px] font-semibold text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isPhone ? '-my-2 min-h-[44px]' : 'hover:underline',
      )}
      data-testid="morning-brief-expand"
    >
      {expanded ? 'Show less' : 'Read all'}
      {expanded ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
    </button>
  );

  return (
    <section
      aria-labelledby="morning-brief-title"
      aria-busy={stale || undefined}
      className={cn(
        'flex items-start gap-3 rounded-card border border-[color:var(--brand-soft-border)] bg-brand-soft px-4 py-3.5 transition-opacity sm:gap-4 sm:px-5 sm:py-4',
        stale && 'opacity-60',
      )}
      data-testid="morning-brief"
      data-ai={aiText ? 'true' : 'false'}
      data-scope={brief.scope}
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-surface text-brand-ink shadow-card" aria-hidden="true">
        <Sparkles className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 flex-1">
        {isPhone ? (
          <div className="flex items-center justify-between gap-2">
            <h2 id="morning-brief-title" className="flex min-w-0 items-center gap-2 text-[14px] font-bold text-ink">
              Morning brief
              {briefMine ? (
                <span className="rounded-full bg-surface px-2 py-px text-[11px] font-semibold text-ink-2" data-testid="morning-brief-scope">
                  Your projects
                </span>
              ) : null}
            </h2>
            {items.length || aiText ? toggle : null}
          </div>
        ) : (
          <div className="flex flex-wrap items-baseline gap-x-3">
            <h2 id="morning-brief-title" className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-3">{label}</h2>
            {canExpandDesktop ? toggle : null}
          </div>
        )}
        <p
          id="morning-brief-text"
          ref={textRef}
          className={cn(
            'mt-1 text-[13.5px] leading-[1.55] text-ink sm:text-[14px]',
            isPhone && !expanded && 'line-clamp-3',
            clampDesktop && 'line-clamp-2',
          )}
          data-testid="morning-brief-text"
        >
          {aiText || (items.length
            ? <BriefSentence items={items} detailed={isPhone || expanded ? Infinity : DETAILED_FACTS} />
            : 'Nothing needs a decision this morning.')}
          {brief.more > 0 && (!isPhone || expanded) ? (
            <span className="text-ink-3"> +{brief.more} more in Needs you today.</span>
          ) : null}
        </p>
        {isPhone && expanded ? (
          <>
            <p className="mt-1 text-[12px] text-ink-3">{label}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {brief.can_ask_ai ? (
                <Button asChild variant="outline" size="sm" className="h-10" data-testid="morning-brief-ask-ai">
                  <Link to={askLink}><Sparkles aria-hidden="true" />Ask AI</Link>
                </Button>
              ) : null}
              <Button type="button" variant="ghost" size="sm" className="h-10 text-ink-2" onClick={hide} data-testid="morning-brief-dismiss">
                Hide until tomorrow
              </Button>
            </div>
          </>
        ) : null}
      </div>
      <div className="-mr-1.5 -mt-1 flex shrink-0 items-center gap-0.5">
        {brief.can_ask_ai && !isPhone ? (
          <Button asChild variant="ghost" size="sm" className="text-brand-ink" data-testid="morning-brief-ask-ai">
            <Link to={askLink}><Sparkles aria-hidden="true" />Ask AI</Link>
          </Button>
        ) : null}
        {!isPhone ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={hide}
            aria-label="Hide the morning brief until tomorrow"
            title="Hide until tomorrow"
            className="text-ink-3"
            data-testid="morning-brief-dismiss"
          >
            <X aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </section>
  );
}
